import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import firebase from 'firebase/compat';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { PeriodBarResult } from '../../../ui/components/period-bar/period-bar.component';
import { PatientId } from '../../patient/domain/patient';
import { BillId, BillStatus } from '../domain/bill/bill';
import {
  BillLineDescription,
  BillLineDescriptionProps,
} from '../domain/bill/bill-line-description';
import { Credit, CreditStatus } from '../domain/credit/credit';
import { CreditNotFoundException } from '../domain/credit/credit-exceptions';
import { CreditLine } from '../domain/credit/credit-line';
import { DietitianId } from '../domain/dietitian';
import { InnerThirdPartySchema } from './estimate.repository';
import { ThirdPartyRepository } from './third-party.repository';
import Query = firebase.firestore.Query;

export interface CreditLineSchema {
  creditId: string;
  descriptionId: string;
  comment: string | null;
  amount: number;
  date: string | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface CreditSchema {
  patientId: string | null;
  customerFullname: string | null;
  thirdParty: InnerThirdPartySchema | null;
  referencePrefix: string | null;
  reference: number | null;
  creditedAt: Timestamp | null;
  lines: CreditLineSchema[];
  amountExclTax: number;
  amountInclTax: number;
  amountTax: number;
  rateTax: number | null;
  withTax: boolean;
  emittedAt: Timestamp | null;
  paidAt: Timestamp | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  archived: boolean;
  archivedAt: Timestamp | null;
  status: CreditStatus;
  url: string | null;
  path: string | null;
  documentHasReady: boolean;
  billId: string | null;
}

export interface ResultCalcTot {
  count: number;
  amountIncludingTax: number;
  amountExcludingTax: number;
}

@Injectable()
export class CreditRepository {
  private readonly calcTotalCredit: (
    data: unknown,
  ) => Observable<ResultCalcTot>;
  private readonly previewPdf: (data: unknown) => Observable<string>;

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
  ) {
    this.calcTotalCredit = this.functions.httpsCallable<unknown, ResultCalcTot>(
      'credit-calcTotalCredit',
    );
    this.previewPdf = this.functions.httpsCallable<unknown, string>(
      'credit-previewCredit',
    );
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<CreditSchema>('credits', queryFn);
  }

  toSchema(credit: Credit): CreditSchema {
    return {
      patientId: credit.patientId?.id.toString() ?? null,
      thirdParty: credit.thirdParty
        ? ThirdPartyRepository.innerThirdPartySchemaToThirdParty(
            credit.thirdParty,
          )
        : null,
      customerFullname: credit.customerFullname ?? null,
      referencePrefix: credit.referencePrefix ?? null,
      reference: credit.reference ?? null,
      creditedAt: credit.creditedAt
        ? Timestamp.fromDate(credit.creditedAt)
        : null,
      lines: this.toLineSchema(credit),
      amountExclTax: credit.amountExclTax,
      amountInclTax: credit.amountInclTax,
      amountTax: credit.amountTax,
      rateTax: credit.rateTax ?? null,
      withTax: credit.withTax,
      emittedAt:
        credit.emittedAt !== undefined
          ? Timestamp.fromDate(credit.emittedAt)
          : null,
      paidAt:
        credit.paidAt !== undefined ? Timestamp.fromDate(credit.paidAt) : null,
      createdAt:
        credit.createdAt !== undefined
          ? Timestamp.fromDate(credit.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      archived: credit.archived,
      archivedAt:
        credit.archivedAt !== undefined
          ? Timestamp.fromDate(credit.archivedAt)
          : null,
      status: credit.status,
      url: credit.url ?? null,
      path: credit.path ?? null,
      documentHasReady: credit.documentHasReady,
      billId: credit.billId?.id.toString() ?? null,
    } as CreditSchema;
  }

  private toLineSchema(credit: Credit): CreditLineSchema[] {
    return credit.lines.map((line) => {
      return {
        creditId: credit.id.toString(),
        descriptionId: line.description?.id.toString(),
        comment: line.comment ?? null,
        amount: line.amount,
        date: line.date ?? null,
        createdAt:
          line.createdAt !== undefined
            ? Timestamp.fromDate(line.createdAt)
            : Timestamp.now(),
        updatedAt: Timestamp.now(),
      } as CreditLineSchema;
    });
  }

  fromSchema(schema: CreditSchema, dietitianId: string, id?: string): Credit {
    return Credit.create(
      {
        dietitianId: DietitianId.create(new UniqueEntityID(dietitianId)),
        patientId: schema.patientId
          ? PatientId.create(new UniqueEntityID(schema.patientId))
          : undefined,
        thirdParty: schema.thirdParty
          ? ThirdPartyRepository.thirdPartyFormInnerThirdPartySchema(
              schema.thirdParty,
            )
          : undefined,
        customerFullname: schema.customerFullname ?? undefined,
        referencePrefix: schema.referencePrefix ?? undefined,
        reference: schema.reference ?? undefined,
        creditedAt: schema.creditedAt?.toDate() ?? undefined,
        lines: this.fromLineSchema(schema.lines),
        amountExclTax: schema.amountExclTax,
        amountInclTax: schema.amountInclTax,
        amountTax: schema.amountTax,
        rateTax: schema.rateTax ?? undefined,
        withTax: schema.withTax,
        emittedAt: schema.emittedAt?.toDate() ?? undefined,
        paidAt: schema.emittedAt?.toDate() ?? undefined,
        archived: schema.archived,
        archivedAt: schema.emittedAt ? schema.emittedAt?.toDate() : undefined,
        createdAt: schema.createdAt?.toDate() ?? undefined,
        updatedAt: schema.updatedAt?.toDate() ?? undefined,
        status: schema.status,
        url: schema.url ?? undefined,
        path: schema.path ?? undefined,
        documentHasReady: schema.documentHasReady,
        billId: schema.billId
          ? BillId.create(new UniqueEntityID(schema.billId))
          : undefined,
      },
      new UniqueEntityID(id),
    );
  }

  private fromLineSchema(lines: CreditLineSchema[]): CreditLine[] {
    return lines.map((line) => {
      return CreditLine.create({
        description: BillLineDescription.create(
          {} as BillLineDescriptionProps,
          new UniqueEntityID(line.descriptionId),
        ),
        comment: line.comment ?? undefined,
        amount: line.amount,
        date: line.date ?? undefined,
        createdAt: line.createdAt?.toDate() ?? undefined,
        updatedAt: line.updatedAt?.toDate() ?? undefined,
      });
    });
  }

  async create(credit: Credit): Promise<Credit> {
    const schema = this.toSchema(credit);
    if (credit.dietitianId) {
      // Reference
      const now = new Date();
      schema.referencePrefix =
        now.getFullYear().toString() +
        (now.getMonth() + 1 < 10
          ? '0' + (now.getMonth() + 1)
          : now.getMonth() + 1);
      const lastReference = await this.findLastReferenceByReferencePrefix(
        credit.dietitianId.id.toString(),
        schema.referencePrefix,
      );
      if (lastReference && lastReference.length === 1) {
        schema.reference = lastReference[0].reference
          ? lastReference[0].reference + 1
          : null;
      } else {
        schema.reference = 1;
      }

      const ref = await this.collection(credit.dietitianId.id.toString()).add(
        schema,
      );
      return this.fromSchema(schema, credit.dietitianId.id.toString(), ref.id);
    } else {
      return Promise.reject('Diététicien non identifié');
    }
  }

  async save(credit: Credit): Promise<Credit> {
    const schema = this.toSchema(credit);
    if (credit.dietitianId) {
      if (schema.status === 'EMITTED' || schema.status === 'PAID') {
        // Reference
        if (!schema.referencePrefix && !schema.reference) {
          const now = new Date();
          schema.referencePrefix =
            now.getFullYear().toString() +
            (now.getMonth() + 1 < 10
              ? '0' + (now.getMonth() + 1)
              : now.getMonth() + 1);
          const lastReference = await this.findLastReferenceByReferencePrefix(
            credit.dietitianId.id.toString(),
            schema.referencePrefix,
          );
          if (lastReference && lastReference.length === 1) {
            schema.reference = lastReference[0].reference
              ? lastReference[0].reference + 1
              : null;
          } else {
            schema.reference = 1;
          }
        }
        // Calcul des totaux
        schema.amountExclTax = 0;
        schema.amountTax = 0;
        schema.amountInclTax = 0;
        const taxFactor = schema.withTax ? Number(schema.rateTax) / 100 : 0;
        for (const line of schema.lines) {
          schema.amountExclTax += Number(line.amount);
          schema.amountTax += Number(line.amount) * taxFactor;
          schema.amountInclTax +=
            Number(line.amount) + Number(line.amount) * taxFactor;
        }
      }

      const dietitianId = credit.dietitianId.id.toString();
      await this.collection(dietitianId)
        .doc(credit.creditId.id.toString())
        .set(schema);
      return this.fromSchema(
        schema,
        dietitianId,
        credit.creditId.id.toString(),
      );
    } else {
      return Promise.reject('Diététicien non identifié');
    }
  }

  async load(dietitianId: string, creditId: string): Promise<Credit> {
    const snap = await firstValueFrom(
      this.collection(dietitianId).doc(creditId).get(),
    );
    if (!snap.exists || snap.data() == null) {
      throw new CreditNotFoundException();
    }
    const schema = snap.data() as CreditSchema;
    return this.fromSchema(schema, dietitianId, snap.id);
  }

  async findAllActivated(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    status: CreditStatus | undefined,
    orderBy: string,
    lastId?: string | undefined,
  ): Promise<Credit[]> {
    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );

      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          // Archived
          let qry = ref.where('archived', '==', false);

          // Filter
          qry = this.doFilter(period, status, qry);

          // OrderBy
          qry = this.doOrderBy(period, orderBy, qry);

          return qry.startAfter(last).limit(30);
        }).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data(), dietitianId, doc.id),
      );
    } else {
      try {
        const snap = await firstValueFrom(
          this.collection(dietitianId, (ref) => {
            // Archived
            let qry = ref.where('archived', '==', false);

            // Filter
            qry = this.doFilter(period, status, qry);

            // OrderBy
            qry = this.doOrderBy(period, orderBy, qry);

            return qry.limit(30);
          }).get(),
        );
        return snap.docs.map((doc) =>
          this.fromSchema(doc.data(), dietitianId, doc.id),
        );
      } catch (e) {
        console.log('❌', e);
        return [];
      }
    }
  }

  private doFilter(
    period: PeriodBarResult | undefined,
    status: BillStatus | CreditStatus | undefined,
    qry: Query,
  ): Query {
    if (status) {
      qry = qry.where('status', '==', status);
    }
    if (period) {
      if (period.start) {
        qry = qry.where('creditedAt', '>=', period.start.toDate());
      }
      if (period.end) {
        qry = qry.where('creditedAt', '<=', period.end.toDate());
      }
    }
    return qry;
  }

  private doOrderBy(
    period: PeriodBarResult | undefined,
    orderBy: string,
    qry: Query,
  ): Query {
    if (orderBy === 'ref@desc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('creditedAt', 'desc');
      }
      qry = qry.orderBy('referencePrefix', 'desc').orderBy('reference', 'desc');
    } else if (orderBy === 'ref@asc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('creditedAt', 'desc');
      }
      qry = qry.orderBy('referencePrefix', 'asc').orderBy('reference', 'asc');
    } else if (orderBy === 'billedAt@desc') {
      qry = qry.orderBy('creditedAt', 'desc');
    } else if (orderBy === 'billedAt@asc') {
      qry = qry.orderBy('creditedAt', 'asc');
    } else if (orderBy === 'amountExclTax@desc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('creditedAt', 'desc');
      }
      qry = qry.orderBy('amountExclTax', 'desc');
    } else if (orderBy === 'amountExclTax@asc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('creditedAt', 'desc');
      }
      qry = qry.orderBy('amountExclTax', 'asc');
    }
    return qry;
  }

  async findAllArchived(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    lastId: string | undefined,
  ) {
    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          let qry = ref.where('archived', '==', true);
          if (period) {
            if (period.start) {
              qry = qry.where('creditedAt', '>=', period.start.toDate());
            }
            if (period.end) {
              qry = qry.where('creditedAt', '<=', period.end.toDate());
            }
          }
          qry = qry.orderBy('updatedAt', 'desc').startAfter(last).limit(30);
          return qry;
        }).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data(), dietitianId, doc.id),
      );
    } else {
      try {
        const snap = await firstValueFrom(
          this.collection(dietitianId, (ref) => {
            let qry = ref.where('archived', '==', true);
            if (period) {
              if (period.start) {
                qry = qry.where('creditedAt', '>=', period.start.toDate());
              }
              if (period.end) {
                qry = qry.where('creditedAt', '<=', period.end.toDate());
              }
              if (period.start || period.end) {
                qry = qry.orderBy('creditedAt', 'desc');
              }
            }
            qry = qry.orderBy('updatedAt', 'desc').limit(30);
            return qry;
          }).get(),
        );
        return snap.docs.map((doc) =>
          this.fromSchema(doc.data(), dietitianId, doc.id),
        );
      } catch (e) {
        console.log('❌', e);
        return [];
      }
    }
  }

  async calcTotalCreditFromCloud(
    archived: boolean,
    start: Date | undefined,
    end: Date | undefined,
    status: CreditStatus | undefined,
  ): Promise<ResultCalcTot> {
    const data = {
      archived,
      start,
      end,
      status,
    };
    return firstValueFrom(this.calcTotalCredit(data));
  }

  async previewPdfFromCloud(): Promise<string> {
    return firstValueFrom(this.previewPdf({}));
  }

  creditValueChanges(
    dietitianId: string,
    creditId: string,
  ): Observable<CreditSchema | undefined> {
    return this.collection(dietitianId).doc(creditId).valueChanges();
  }

  async findAllExported(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    status: CreditStatus | undefined,
    isArchived: boolean,
  ): Promise<Credit[]> {
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) => {
        let qry = ref.where('archived', '==', isArchived);
        if (status) {
          qry = qry.where('status', '==', status);
        }
        if (period) {
          if (period.start) {
            qry = qry.where('creditedAt', '>=', period.start.toDate());
          }
          if (period.end) {
            qry = qry.where('creditedAt', '<=', period.end.toDate());
          }
          if (period.start || period.end) {
            qry = qry.orderBy('creditedAt', 'desc');
          }
        }
        qry = qry.orderBy('updatedAt', 'desc');
        return qry;
      }).get(),
    );
    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }

  private async findLastReferenceByReferencePrefix(
    dietitianId: string,
    referencePrefix: string | undefined,
  ) {
    let snap;
    if (referencePrefix) {
      snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref
            .where('referencePrefix', '==', referencePrefix)
            .orderBy('reference', 'desc')
            .limit(1),
        ).get(),
      );
    } else {
      snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref.orderBy('reference', 'desc').limit(1),
        ).get(),
      );
    }
    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }
}
